import { IconHistory } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams
} from "react-router";
import api from "../../../api";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import type { Site } from "../../../utils/backend-types";
import { MultiConfirmationModal } from "../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import {
  DEFAULT_SUBPAGE as EDIT_CONFIGURATION_MODAL_DEFAULT_SUBPAGE,
  EditConfigurationModal
} from "../EditConfigurationModal/EditConfigurationModal";
import { ImportLogsModal } from "../ImportLogsModal/ImportLogsModal";
import { NewConfigurationModal } from "../NewConfigurationModal/NewConfigurationModal";
import type { ConfigurationData } from "../ThirdPartySystems.types";
import { getConfigurationDisplayName } from "../utils/getConfigurationDisplayName";
import { getSystemDisplayNameFromValue } from "../utils/getSystemDisplayNameFromValue";

interface ThirdPartySystemsListProps {
  sites: Array<Site>;
  subMeteringConfigurations: Array<ConfigurationData>;
  variantId: number;
  onConfigurationCreated: (configuration: ConfigurationData) => void;
  onConfigurationsDeleted: (ids: Array<number>) => void;
  onConfigurationUpdated: (updatedConfigurationData: ConfigurationData) => void;
}

function ThirdPartySystemsList({
  sites,
  subMeteringConfigurations,
  variantId,
  onConfigurationCreated,
  onConfigurationsDeleted,
  onConfigurationUpdated
}: ThirdPartySystemsListProps) {
  const [configurationsToDelete, setConfigurationsToDelete] = useState<
    Array<ConfigurationData>
  >([]);

  const navigate = useNavigate();
  const { showErrorToast } = useToast();

  const onToggleDeleteConfigurationsModal = () => setConfigurationsToDelete([]);

  function handleClickDeleteSelection(
    selectedConfigurations: Array<ConfigurationData>
  ) {
    setConfigurationsToDelete(selectedConfigurations);
  }

  const handleClickDelete = useCallback(
    (systemId: number) => {
      const systemToDelete = subMeteringConfigurations.find(
        (system) => system.id === systemId
      );

      if (systemToDelete) {
        setConfigurationsToDelete([systemToDelete]);
      }
    },
    [subMeteringConfigurations]
  );

  async function handleDeleteSelectedConfigurations(ids: Array<number>) {
    const deletePromises = ids.map((id) =>
      api.delete(urls.api.subMeteringConfiguration(id))
    );

    return Promise.all(deletePromises)
      .then(() => {
        onConfigurationsDeleted(ids);
        mrtTable.setRowSelection({});
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        setConfigurationsToDelete([]);
      });
  }

  const configurationsToDeleteModalIsOpen = configurationsToDelete.length > 0;
  const configurationsToDeleteNames = configurationsToDelete.map((system) =>
    getConfigurationDisplayName(system.name, system.subMeteringSystem)
  );
  const configurationsToDeleteIds = configurationsToDelete.map(
    (system) => system.id
  );

  const ElementEditConfigurationsModalWrapper = (
    <EditConfigurationsModalWrapper
      sites={sites}
      subMeteringConfigurations={subMeteringConfigurations}
      variantId={variantId}
      onConfigurationUpdated={onConfigurationUpdated}
    />
  );

  const columns: Array<MRT_ColumnDef<ConfigurationData>> = [
    {
      header: "Name",
      grow: true,
      enableCellHoverReveal: true,
      accessorFn: (row) =>
        getConfigurationDisplayName(row.name, row.subMeteringSystem),
      Cell: ({ row }) => {
        const path = `./${row.original.id}/${EDIT_CONFIGURATION_MODAL_DEFAULT_SUBPAGE}`;
        const displayName = getConfigurationDisplayName(
          row.original.name,
          row.original.subMeteringSystem
        );

        return <Link to={path}>{displayName}</Link>;
      }
    },
    {
      header: "System",
      size: 300,
      grow: false,
      accessorFn: (row) => getSystemDisplayNameFromValue(row.subMeteringSystem)
    }
  ];

  const mrtTable = useMantineReactTable<ConfigurationData>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: "Es gibt noch keine Konfigurationen.",
      hideBorder: true,
      initialState: { showGlobalFilter: true }
    }),
    ...getMRTTopToolbarCustomActionsRenderer({
      create: {
        text: "Neue Konfiguration einrichten",
        onClick: () => navigate("./neu")
      },
      delete: {
        text: (selectedRows: Array<ConfigurationData>) => {
          return `Konfiguration${selectedRows.length > 1 ? "en" : ""} löschen`;
        },
        onClick: (rowSelection) =>
          handleClickDeleteSelection(
            subMeteringConfigurations.filter(
              (configuration) => rowSelection[configuration.id]
            )
          )
      }
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: {
          text: "System bearbeiten",
          onClick: (row) => {
            mrtTable.setEditingRow(null);
            navigate(
              `./${row.original.id}/${EDIT_CONFIGURATION_MODAL_DEFAULT_SUBPAGE}`
            );
          }
        },
        delete: {
          text: "System löschen",
          onClick: (row) => handleClickDelete(row.original.id)
        },
        other: [
          {
            text: "Import-Historie anzeigen",
            icon: IconHistory,
            onClick: (row) => {
              navigate(`./${row.original.id}/historie`);
            }
          }
        ]
      },
      displayColumnDefOptions: {
        "mrt-row-select": {
          size: 40,
          minSize: 40
        },
        "mrt-row-actions": {
          size: 100,
          grow: false
        }
      }
    }),
    layoutMode: "grid",
    enableGlobalFilter: true,
    globalFilterFn: "includesString",
    columns: columns,
    data: subMeteringConfigurations
  });

  return (
    <div className="ThirdPartySystemsList">
      <MantineReactTable table={mrtTable} />
      <MultiConfirmationModal
        actionName="löschen"
        actionObjects={configurationsToDeleteNames}
        confirmationText="Möchten Sie die folgenden Konfigurationen löschen? Dieser Schritt kann nicht rückgängig gemacht werden."
        isModalOpen={configurationsToDeleteModalIsOpen}
        objectName="Konfigurationen"
        toggleModal={onToggleDeleteConfigurationsModal}
        onAction={() =>
          handleDeleteSelectedConfigurations(configurationsToDeleteIds)
        }
      />
      <Routes>
        <Route
          element={
            <NewConfigurationModalWrapper
              sites={sites}
              variantId={variantId}
              onConfigurationCreated={onConfigurationCreated}
            />
          }
          path="neu"
        />
        <Route path=":id">
          <Route
            element={
              <Navigate replace to={EDIT_CONFIGURATION_MODAL_DEFAULT_SUBPAGE} />
            }
            index
          />
          <Route element={<LogsModalWrapper sites={sites} />} path="historie" />
          <Route
            element={ElementEditConfigurationsModalWrapper}
            path=":subPage"
          >
            <Route element={ElementEditConfigurationsModalWrapper} index />
          </Route>
        </Route>
      </Routes>
    </div>
  );
}

function NewConfigurationModalWrapper({
  sites,
  variantId,
  onConfigurationCreated
}: {
  sites: Array<Site>;
  variantId: number;
  onConfigurationCreated: (configuration: ConfigurationData) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsOpen(true);
  }, []);

  function handleToggle() {
    setIsOpen(!isOpen);
    navigate(`./..`);
  }

  return (
    <NewConfigurationModal
      isOpen={isOpen}
      sites={sites}
      variantId={variantId}
      onConfigurationCreated={onConfigurationCreated}
      onToggle={handleToggle}
    />
  );
}

function EditConfigurationsModalWrapper({
  sites,
  subMeteringConfigurations,
  variantId,
  onConfigurationUpdated
}: {
  sites: Array<Site>;
  subMeteringConfigurations: Array<ConfigurationData>;
  variantId: number;
  onConfigurationUpdated: (updatedConfigurationData: ConfigurationData) => void;
}) {
  const params = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (params.id) {
      setIsOpen(true);
    }
  }, [params.id]);

  const id = Number(params.id);
  const configuration = subMeteringConfigurations.find(
    (configuration) => configuration.id === id
  );
  if (!configuration) {
    return null;
  }

  function handleToggle() {
    setIsOpen(!isOpen);
    navigate(`../..`);
  }

  return (
    <EditConfigurationModal
      configuration={configuration}
      isOpen={isOpen}
      sites={sites}
      variantId={variantId}
      onConfigurationUpdated={onConfigurationUpdated}
      onToggle={handleToggle}
    />
  );
}

function LogsModalWrapper({ sites }: { sites: Array<Site> }) {
  const params = useParams();
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();

  const configurationId = Number(params.id);
  if (!configurationId) {
    return null;
  }

  function handleToggle() {
    setIsOpen(!isOpen);
    navigate(`../..`);
  }

  return (
    <ImportLogsModal
      configurationId={configurationId}
      isOpen={isOpen}
      sites={sites}
      onToggle={handleToggle}
    />
  );
}

export { ThirdPartySystemsList, ThirdPartySystemsListProps };
